export const USAGE_CATEGORIES = Object.freeze({
  TTS: 'tts',
  CONTENT: 'content',
  COURSE_GENERATION: 'course_generation',
  IMAGE: 'image',
  // Google Search grounding, billed per search query on Gemini 3+ (per grounded prompt on 2.5).
  SEARCH: 'search',
});

export const USAGE_UNITS = Object.freeze({
  CHARACTERS: 'characters',
  TOKENS: 'tokens',
  IMAGES: 'images',
  REQUESTS: 'requests',
  SEARCH_QUERIES: 'search_queries',
});

const PAGE_DEFINITIONS = Object.freeze([
  { key: 'dashboard', label: 'Dashboard', exact: ['/dashboard'] },
  { key: 'ai_avatar', label: 'AI Avatar', exact: ['/dashboard/xr/ai-avatar'] },
  { key: 'language_lab', label: 'Language Lab', exact: ['/language-lab'] },
  { key: 'language_lab_practice', label: 'Language Lab Practice', exact: ['/language-lab/practice'] },
  { key: 'language_lab_writing', label: 'Language Writing Lab', exact: ['/language-lab/writing'] },
  { key: 'language_lab_tutor', label: 'Language Tutor Lab', exact: ['/language-lab/tutor'] },
  { key: 'study_area', label: 'Study Area', prefix: ['/study_area/'] },
  { key: 'course_detail', label: 'Course Detail', prefix: ['/courses/'] },
  { key: 'visualize', label: 'Visualize', exact: ['/visualize'] },
  { key: 'course_editor', label: 'Course Editor', exact: ['/course-editor'] },
  { key: 'admin_create_course', label: 'Create Course', exact: ['/admin/create-course'] },
  { key: 'admin_edit_course', label: 'Edit Course', prefix: ['/admin/edit-course/'] },
  { key: 'create_xr_course', label: 'Create XR Course', exact: ['/create-xr-course'] },
  { key: 'edit_xr_course', label: 'Edit XR Course', prefix: ['/edit-xr-course/'] },
  { key: 'assessment_engine', label: 'Assessment Engine', prefix: ['/assessment-engine'] },
  { key: 'learning_delivery', label: 'Learning Delivery', exact: ['/learning-delivery'] },
  { key: 'admin_dashboard', label: 'Admin Dashboard', exact: ['/admin'] },
  { key: 'background_job', label: 'Background Job', exact: [] },
  { key: 'interactive_animations', label: 'Animated Activities', exact: [] },
  { key: 'adaptive_learning', label: 'Adaptive Learning', exact: ['/adaptive'], prefix: ['/adaptive/'] },
  { key: 'unknown', label: 'Unknown Surface', exact: [] },
]);

const FEATURE_DEFINITIONS = Object.freeze({
  dashboard_ask_ai: { label: 'Dashboard Ask AI', pageKey: 'dashboard', usageCategory: USAGE_CATEGORIES.CONTENT },
  dashboard_ask_ai_image: { label: 'Dashboard Ask AI Images', pageKey: 'dashboard', usageCategory: USAGE_CATEGORIES.IMAGE },
  ai_avatar_chat: { label: 'AI Avatar Chat', pageKey: 'ai_avatar', usageCategory: USAGE_CATEGORIES.TTS },
  ai_avatar_conversations: { label: 'AI Avatar Conversations', pageKey: 'ai_avatar', usageCategory: USAGE_CATEGORIES.CONTENT },
  language_lab_reference: { label: 'Language Lab Reference', pageKey: 'language_lab_practice', usageCategory: USAGE_CATEGORIES.TTS },
  language_lab_practice_prompt: { label: 'Practice Prompt', pageKey: 'language_lab_practice', usageCategory: USAGE_CATEGORIES.CONTENT },
  language_lab_writing_analysis: { label: 'Writing Analysis', pageKey: 'language_lab_writing', usageCategory: USAGE_CATEGORIES.CONTENT },
  language_lab_writing_prompt: { label: 'Writing Prompt', pageKey: 'language_lab_writing', usageCategory: USAGE_CATEGORIES.CONTENT },
  language_lab_feedback_translation: { label: 'Feedback Translation', pageKey: 'language_lab_writing', usageCategory: USAGE_CATEGORIES.CONTENT },
  language_lab_ai_tutor: { label: 'AI Tutor', pageKey: 'language_lab_tutor', usageCategory: USAGE_CATEGORIES.CONTENT },
  study_area_reader: { label: 'Study Area Reader', pageKey: 'study_area', usageCategory: USAGE_CATEGORIES.TTS },
  hear_me_out_player: { label: 'Hear Me Out Player', pageKey: 'study_area', usageCategory: USAGE_CATEGORIES.TTS },
  hear_me_out_story_generation: { label: 'Hear Me Out Story Generation', pageKey: 'study_area', usageCategory: USAGE_CATEGORIES.TTS },
  hear_me_out_preview: { label: 'Hear Me Out Preview', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.TTS },
  dictation_activity_prompt: { label: 'Dictation Prompt', pageKey: 'study_area', usageCategory: USAGE_CATEGORIES.TTS },
  course_prefetch_tts: { label: 'Offline Course Prefetch TTS', pageKey: 'course_detail', usageCategory: USAGE_CATEGORIES.TTS },
  course_tts_preview: { label: 'Course TTS Preview', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.TTS },
  course_tts_generate: { label: 'Course TTS Generate', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.TTS },
  course_submodule_tts_generate: { label: 'Course Submodule TTS Generate', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.TTS },
  audio_summary_generation: { label: 'Audio Summary Generation', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.TTS },
  module_audio_summary_job: { label: 'Module Audio Summary Job', pageKey: 'background_job', usageCategory: USAGE_CATEGORIES.TTS },
  enhanced_quiz_audio_qa: { label: 'Enhanced Quiz Audio QA', pageKey: 'assessment_engine', usageCategory: USAGE_CATEGORIES.TTS },
  xr_audio: { label: 'XR Audio', pageKey: 'create_xr_course', usageCategory: USAGE_CATEGORIES.TTS },
  xr_scene_audio: { label: 'XR Scene Audio', pageKey: 'create_xr_course', usageCategory: USAGE_CATEGORIES.TTS },
  xr_submodule_audio: { label: 'XR Submodule Audio', pageKey: 'create_xr_course', usageCategory: USAGE_CATEGORIES.TTS },
  xr_annotation_audio: { label: 'XR Annotation Audio', pageKey: 'create_xr_course', usageCategory: USAGE_CATEGORIES.TTS },
  visualize_book_uploads: { label: 'Visualize Uploads', pageKey: 'visualize', usageCategory: USAGE_CATEGORIES.CONTENT },
  visualize_analyze: { label: 'Visualize Analysis', pageKey: 'visualize', usageCategory: USAGE_CATEGORIES.CONTENT },
  visualize_image_generation: { label: 'Visualize Images', pageKey: 'visualize', usageCategory: USAGE_CATEGORIES.IMAGE },
  thumbnail_prompt: { label: 'Thumbnail Prompt', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.CONTENT },
  generate_thumbnail: { label: 'Generate Thumbnail', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.IMAGE },
  generate_thumbnail_batch: { label: 'Generate Thumbnail Batch', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.IMAGE },
  category_icon_generation: { label: 'Category Icon Generation', pageKey: 'background_job', usageCategory: USAGE_CATEGORIES.CONTENT },
  auto_flow_modules: { label: 'Auto Course Modules', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  auto_flow_pages: { label: 'Auto Course Pages', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  auto_flow_pages_retry: { label: 'Auto Course Pages Retry', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  create_course_generations: { label: 'Create Course Generations', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  question_bank_generation: { label: 'Question Bank Generation', pageKey: 'assessment_engine', usageCategory: USAGE_CATEGORIES.CONTENT },
  question_bank_generate: { label: 'Question Bank Generation', pageKey: 'assessment_engine', usageCategory: USAGE_CATEGORIES.CONTENT },
  admin_corner_generation: { label: 'Admin Corner Generation', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.CONTENT },
  translate_with_gemini: { label: 'Gemini Translation', pageKey: 'study_area', usageCategory: USAGE_CATEGORIES.CONTENT },
  'activity-plan': { label: 'Activity Plan', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.CONTENT },
  'manual-structure-preview': { label: 'Manual Structure Preview', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'manual-flow-pages': { label: 'Manual Flow Pages', pageKey: 'admin_edit_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'single-page-regeneration': { label: 'Single Page Regeneration', pageKey: 'admin_edit_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'auto-flow-pages-full-retry': { label: 'Auto Course Pages Full Retry', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'manual-scenario': { label: 'Manual Scenario', pageKey: 'admin_edit_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'manual-quiz': { label: 'Manual Quiz', pageKey: 'admin_edit_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'xr-structure-generation': { label: 'XR Structure Generation', pageKey: 'create_xr_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'xr-auto-course': { label: 'XR Auto Course', pageKey: 'create_xr_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  'predictive-insights': { label: 'Predictive Insights', pageKey: 'course_editor', usageCategory: USAGE_CATEGORIES.CONTENT },
  // Media produced as part of course creation: category stays course_generation (so a
  // course's full generation cost is one filter), the feature says what was made, and
  // usage_unit (images vs tokens) tells the tracker how to count it.
  course_gen_illustration: { label: 'Course Gen: Illustrations', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  course_gen_infographic: { label: 'Course Gen: Infographics', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  course_gen_page_image: { label: 'Course Gen: Page Lead Images', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  course_gen_thumbnail: { label: 'Course Gen: Thumbnail Image', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  course_gen_thumbnail_prompt: { label: 'Course Gen: Thumbnail Prompt', pageKey: 'admin_create_course', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  interactive_plan: { label: 'Animated Activities: Planning', pageKey: 'interactive_animations', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  interactive_verify: { label: 'Animated Activities: Verification', pageKey: 'interactive_animations', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  interactive_generate: { label: 'Animated Activities: Generation', pageKey: 'interactive_animations', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  interactive_refine: { label: 'Animated Activities: Refine and Repair', pageKey: 'interactive_animations', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  interactive_judge: { label: 'Animated Activities: Quality Judge', pageKey: 'interactive_animations', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
  adaptive_learning: { label: 'Adaptive Journeys', pageKey: 'adaptive_learning', usageCategory: USAGE_CATEGORIES.CONTENT },
  role_skill_mapping_generation: { label: 'Role Skill Mapping', pageKey: 'background_job', usageCategory: USAGE_CATEGORIES.CONTENT },
  course_skill_mapping_generation: { label: 'Course Skill Mapping', pageKey: 'background_job', usageCategory: USAGE_CATEGORIES.COURSE_GENERATION },
});

/** What a course-creation media call produced -> the feature key it is tracked under. */
export const COURSE_GENERATION_STAGE_FEATURES = Object.freeze({
  illustration: 'course_gen_illustration',
  infographic: 'course_gen_infographic',
  page_image: 'course_gen_page_image',
  thumbnail: 'course_gen_thumbnail',
  thumbnail_prompt: 'course_gen_thumbnail_prompt',
});

/**
 * Re-labels a usage context as course-creation work: category becomes
 * course_generation and the feature becomes the specific stage (see above).
 * Everything else (endpoint, page, metadata incl. course id/title) is kept.
 * Unknown stages leave the feature unchanged.
 */
export function toCourseGenerationUsage(usageContext, stage) {
  const base = usageContext || {};
  return {
    ...base,
    featureKey: COURSE_GENERATION_STAGE_FEATURES[stage] || base.featureKey,
    usageCategory: USAGE_CATEGORIES.COURSE_GENERATION,
  };
}

function trimTrailingSlash(value) {
  if (!value || value === '/') return '/';
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

export function normalizeUsageKey(rawValue, fallback = 'unknown') {
  const value = String(rawValue || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return value || fallback;
}

export function normalizePagePath(rawValue) {
  if (!rawValue) return null;
  try {
    const asString = String(rawValue).trim();
    const url = asString.startsWith('http://') || asString.startsWith('https://')
      ? new URL(asString)
      : new URL(asString, 'http://localhost');
    return trimTrailingSlash(url.pathname || '/');
  } catch {
    const path = String(rawValue || '').trim();
    if (!path) return null;
    const withoutQuery = path.split('?')[0].split('#')[0];
    return trimTrailingSlash(withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`);
  }
}

export function getFeatureUsageDefinition(featureKey) {
  const normalizedKey = normalizeUsageKey(featureKey);
  return FEATURE_DEFINITIONS[normalizedKey] || null;
}

export function getPageDefinition(pageKey) {
  const normalizedKey = normalizeUsageKey(pageKey);
  return PAGE_DEFINITIONS.find((entry) => entry.key === normalizedKey) || PAGE_DEFINITIONS.find((entry) => entry.key === 'unknown');
}

export function getPageDefinitionFromPath(pagePath) {
  const normalizedPath = normalizePagePath(pagePath);
  if (!normalizedPath) return null;
  const exactMatch = PAGE_DEFINITIONS.find((entry) => (entry.exact || []).includes(normalizedPath));
  if (exactMatch) return exactMatch;
  return PAGE_DEFINITIONS.find((entry) => (entry.prefix || []).some((prefix) => normalizedPath.startsWith(prefix))) || null;
}

export function resolvePageContext({ pageKey = null, pagePath = null, featureKey = null } = {}) {
  const normalizedPath = normalizePagePath(pagePath);
  const featureDefinition = getFeatureUsageDefinition(featureKey);
  const explicitPage = pageKey ? getPageDefinition(pageKey) : null;
  const pathPage = normalizedPath ? getPageDefinitionFromPath(normalizedPath) : null;
  const featurePage = featureDefinition?.pageKey ? getPageDefinition(featureDefinition.pageKey) : null;
  const resolvedPage = explicitPage || pathPage || featurePage || getPageDefinition('unknown');
  return {
    pageKey: resolvedPage?.key || 'unknown',
    pageLabel: resolvedPage?.label || 'Unknown Surface',
    pagePath: normalizedPath,
  };
}

export function resolveUsageCategory({ usageCategory = null, featureKey = null } = {}) {
  if (usageCategory) return normalizeUsageKey(usageCategory, USAGE_CATEGORIES.CONTENT);
  return getFeatureUsageDefinition(featureKey)?.usageCategory || USAGE_CATEGORIES.CONTENT;
}

export function resolveFeatureLabel(featureKey) {
  return getFeatureUsageDefinition(featureKey)?.label || null;
}

export function buildBrowserUsageContext(partial = {}) {
  const currentPath = typeof window !== 'undefined' ? window.location?.pathname : null;
  const page = resolvePageContext({
    pageKey: partial.pageKey,
    pagePath: partial.pagePath || currentPath,
    featureKey: partial.featureKey,
  });
  return {
    ...partial,
    featureKey: partial.featureKey ? normalizeUsageKey(partial.featureKey) : undefined,
    usageCategory: resolveUsageCategory({ usageCategory: partial.usageCategory, featureKey: partial.featureKey }),
    pageKey: page.pageKey,
    pagePath: page.pagePath,
  };
}

export function getKnownPageOptions() {
  return PAGE_DEFINITIONS.filter((entry) => entry.key !== 'unknown')
    .map((entry) => ({ key: entry.key, label: entry.label }));
}

